import jwtDecode from 'jwt-decode';

import { STORAGE_KEYS, TOKEN_MANAGER_CONSTANTS } from '@constants';
import { accessToken, refreshToken as refreshTokenStorage } from '@core/storage';
import { Headers } from '@enums';
import { logOut, store, updateToken, updateTokenSuccess } from '@store';
import { JwtPayload } from '@types';
import { API, setHeaders } from '@utils';

interface TokenRefreshState {
  isRefreshing: boolean;
  lastRefreshTime: number;
  tabId: string;
}

interface RefreshPromiseResolver {
  resolve: (value: boolean) => void;
  reject: (reason?: any) => void;
}

class TokenRefreshManager {
  private static instance: TokenRefreshManager;
  private refreshPromise: Promise<boolean> | null = null;
  private refreshResolvers: RefreshPromiseResolver[] = [];
  private readonly tabId: string;
  private readonly storageKey = 'token_refresh_state';
  private readonly lockKey = 'token_refresh_lock';
  private checkInterval: NodeJS.Timeout | null = null;

  private constructor() {
    this.tabId = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    this.initializeEventListeners();
  }

  public static getInstance(): TokenRefreshManager {
    if (!TokenRefreshManager.instance) {
      TokenRefreshManager.instance = new TokenRefreshManager();
    }
    return TokenRefreshManager.instance;
  }

  private initializeEventListeners(): void {
    // Listen for storage events to coordinate between tabs
    window.addEventListener('storage', this.handleStorageChange.bind(this));

    // Listen for beforeunload to clean up
    window.addEventListener('beforeunload', this.cleanup.bind(this));
  }

  private handleStorageChange(event: StorageEvent): void {
    if (event.key === this.storageKey && event.newValue) {
      try {
        const state: TokenRefreshState = JSON.parse(event.newValue);

        // If another tab is refreshing, wait for it
        if (state.isRefreshing && state.tabId !== this.tabId) {
          this.waitForOtherTabRefresh();
        }
      } catch (error) {
        console.error('Error parsing token refresh state:', error);
      }
    }

    // Handle successful token refresh from another tab
    if (event.key === 'access_token' && event.newValue && event.oldValue !== event.newValue) {
      // Token was refreshed by another tab, resolve all pending promises
      this.resolveAllPending(true);
    }

    // Handle logout from another tab
    if (event.key === STORAGE_KEYS.LOGOUT && event.newValue === 'true') {
      this.cleanup();
      store.dispatch(logOut());
    }
  }

  private getRefreshState(): TokenRefreshState | null {
    try {
      const state = localStorage.getItem(this.storageKey);
      return state ? JSON.parse(state) : null;
    } catch {
      return null;
    }
  }

  private setRefreshState(state: TokenRefreshState): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(state));
    } catch (error) {
      console.error('Error setting refresh state:', error);
    }
  }

  private acquireLock(): boolean {
    const now = Date.now();
    const lockData = localStorage.getItem(this.lockKey);

    if (lockData) {
      const { timestamp, tabId } = JSON.parse(lockData);
      // Lock expires after 30 seconds
      if (now - timestamp < 30000 && tabId !== this.tabId) {
        return false;
      }
    }

    localStorage.setItem(
      this.lockKey,
      JSON.stringify({
        tabId: this.tabId,
        timestamp: now,
      }),
    );

    return true;
  }

  private releaseLock(): void {
    const lockData = localStorage.getItem(this.lockKey);
    if (lockData) {
      const { tabId } = JSON.parse(lockData);
      if (tabId === this.tabId) {
        localStorage.removeItem(this.lockKey);
      }
    }
  }

  private async waitForOtherTabRefresh(): Promise<boolean> {
    const maxWaitTime = 30000; // 30 seconds
    const checkInterval = 1000; // Check every second
    const startTime = Date.now();

    while (Date.now() - startTime < maxWaitTime) {
      const state = this.getRefreshState();

      // If no state or refresh is complete, check if we have a valid token
      if (!state || !state.isRefreshing) {
        const token = accessToken.getItem();
        if (token && this.isTokenValid(token)) {
          return true;
        }
        break;
      }

      // Wait before checking again
      await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }

    return false;
  }

  private isTokenValid(token: string): boolean {
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const timeUntilExpiry = decoded.exp * 1000 - Date.now();
      return timeUntilExpiry > 60000; // Valid if more than 1 minute left
    } catch {
      return false;
    }
  }

  private shouldRefreshToken(token: string | null): boolean {
    if (!token) return false;

    try {
      const decoded = jwtDecode<JwtPayload>(token);
      const timeUntilExpiry = decoded.exp * 1000 - Date.now();
      return timeUntilExpiry <= TOKEN_MANAGER_CONSTANTS.REFRESH_THRESHOLD;
    } catch {
      return false;
    }
  }

  private async performTokenRefresh(): Promise<boolean> {
    try {
      const refreshTokenValue = refreshTokenStorage.getItem();
      if (!refreshTokenValue) {
        throw new Error('No refresh token available');
      }

      // Check if refresh token is expired
      try {
        const decoded = jwtDecode<JwtPayload>(refreshTokenValue);
        if (decoded.exp * 1000 <= Date.now()) {
          throw new Error('Refresh token expired');
        }
      } catch (error) {
        throw new Error('Invalid refresh token');
      }

      // Make the refresh API call
      setHeaders({ [Headers.Authorization]: `Bearer ${refreshTokenValue}` });
      const response = await API.post('/user/refresh');

      const { access_token, refresh_token } = response.data;

      if (!access_token || !refresh_token) {
        throw new Error('Invalid token response');
      }

      // Update tokens in storage
      accessToken.setItem(access_token);
      refreshTokenStorage.setItem(refresh_token);

      // Update API headers
      setHeaders({ [Headers.Authorization]: `Bearer ${access_token}` });

      // Update last refresh time
      localStorage.setItem(STORAGE_KEYS.LAST_REFRESH, Date.now().toString());

      // Dispatch success action
      store.dispatch(updateTokenSuccess());

      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }

  public async refreshTokenIfNeeded(): Promise<boolean> {
    const token = accessToken.getItem();

    // If no token or token doesn't need refresh, return
    if (!token) {
      return false;
    }

    if (!this.shouldRefreshToken(token)) {
      return true;
    }

    // If already refreshing, wait for the result
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    // Check if another tab is already refreshing
    const state = this.getRefreshState();
    if (state && state.isRefreshing && state.tabId !== this.tabId) {
      const otherTabResult = await this.waitForOtherTabRefresh();
      if (otherTabResult) {
        return true;
      }
    }

    // Try to acquire lock for refreshing
    if (!this.acquireLock()) {
      // Another tab got the lock, wait for it
      return this.waitForOtherTabRefresh();
    }

    // Create a new refresh promise
    this.refreshPromise = new Promise<boolean>((resolve, reject) => {
      this.refreshResolvers.push({ resolve, reject });
    });

    // Set state to indicate this tab is refreshing
    this.setRefreshState({
      isRefreshing: true,
      lastRefreshTime: Date.now(),
      tabId: this.tabId,
    });

    try {
      // Dispatch the update token action
      store.dispatch(updateToken());

      // Perform the actual refresh
      const success = await this.performTokenRefresh();

      // Update state
      this.setRefreshState({
        isRefreshing: false,
        lastRefreshTime: Date.now(),
        tabId: this.tabId,
      });

      // Release lock
      this.releaseLock();

      // Resolve all pending promises
      this.resolveAllPending(success);

      // Clear the promise
      this.refreshPromise = null;

      return success;
    } catch (error) {
      // Update state on error
      this.setRefreshState({
        isRefreshing: false,
        lastRefreshTime: Date.now(),
        tabId: this.tabId,
      });

      // Release lock
      this.releaseLock();

      // Reject all pending promises
      this.rejectAllPending(error);

      // Clear the promise
      this.refreshPromise = null;

      throw error;
    }
  }

  private resolveAllPending(success: boolean): void {
    const resolvers = [...this.refreshResolvers];
    this.refreshResolvers = [];
    resolvers.forEach(({ resolve }) => resolve(success));
  }

  private rejectAllPending(error: any): void {
    const resolvers = [...this.refreshResolvers];
    this.refreshResolvers = [];
    resolvers.forEach(({ reject }) => reject(error));
  }

  public startPeriodicCheck(): void {
    if (this.checkInterval) {
      return;
    }

    // Initial check after a random delay to prevent all tabs from checking at once
    const initialDelay = Math.random() * 5000 + 1000; // 1-6 seconds
    setTimeout(() => {
      this.refreshTokenIfNeeded();
    }, initialDelay);

    // Set up periodic checking
    this.checkInterval = setInterval(() => {
      this.refreshTokenIfNeeded();
    }, TOKEN_MANAGER_CONSTANTS.CHECK_INTERVAL);
  }

  public stopPeriodicCheck(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  private cleanup(): void {
    this.stopPeriodicCheck();

    // Clean up our lock if we hold it
    this.releaseLock();

    // Clear any refresh state if we were refreshing
    const state = this.getRefreshState();
    if (state && state.tabId === this.tabId && state.isRefreshing) {
      this.setRefreshState({
        isRefreshing: false,
        lastRefreshTime: Date.now(),
        tabId: this.tabId,
      });
    }
  }

  public handleLogout(): void {
    this.cleanup();
    localStorage.setItem(STORAGE_KEYS.LOGOUT, 'true');

    // Clear the logout flag after a short delay
    setTimeout(() => {
      localStorage.removeItem(STORAGE_KEYS.LOGOUT);
    }, 1000);
  }
}

export const tokenRefreshManager = TokenRefreshManager.getInstance();
